/**
 * Auth Types — Users, sessions, roles
 */
import { z } from 'zod';
import { normalizeLoginPhone } from './login-identifier';

// Roles ordered by privilege level (highest first)
export const ROLES = ['admin', 'manager', 'dispatcher', 'crew_lead', 'crew', 'fert_sub', 'viewer', 'service'] as const;
export type Role = typeof ROLES[number];

export interface User {
  id: string;
  company_id: string;
  email: string | null;
  phone?: string | null;
  name: string;
  role: Role;
  is_active: number;
  is_service?: boolean;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
  /**
   * The session token that actually authenticated this request, when auth
   * came from a session (mc_session cookie or Bearer). Absent for non-session
   * auth (X-MC-API-Key) and for the synthetic system/admin user used when
   * AUTH_ENABLED is not 'true'. Routes that rotate a session token MUST only
   * rotate this value, never an arbitrary cookie that may belong to another
   * user.
   */
  authenticatedToken?: string | null;
}

export interface Session {
  id: string;
  user_id: string;
  token: string;
  expires_at: string;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface AuditEntry {
  id: string;
  user_id: string | null;
  user_name: string | null;
  action: string;
  entity_type: string;
  entity_id: string | null;
  changes_json: string | null;
  ip_address: string | null;
  created_at: string;
}

export interface SystemSetting {
  key: string;
  value: string;
  value_type: string;
  category: string;
  label: string;
  description: string | null;
  updated_by: string | null;
  updated_at: string;
}

// Zod schemas for API validation
const phoneSchema = z.string().max(64).transform((value, ctx) => {
  const phone = normalizeLoginPhone(value);
  if (!phone) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Enter a valid mobile number.' });
  return phone ?? z.NEVER;
});

export const LoginSchema = z.object({
  email: z.string().trim().email().transform(value => value.toLowerCase()).optional(),
  phone: phoneSchema.optional(),
  password: z.string().min(1).optional(),
  pin: z.string().regex(/^\d{4,6}$/).optional(),
}).refine(data => Boolean(data.email) !== Boolean(data.phone), {
  message: 'Provide either a mobile number or an email address.',
}).refine(data => Boolean(data.password) !== Boolean(data.pin), {
  message: 'Provide either a password or a PIN.',
});

export const CreateUserSchema = z.object({
  email: z.string().email().optional(),
  phone: phoneSchema.optional(),
  name: z.string().min(1),
  role: z.enum(ROLES).default('viewer'),
  password: z.string().min(6).optional(),
  pin: z.string().min(4).max(6).optional(),
});

export const UpdateUserSchema = z.object({
  name: z.string().min(1).optional(),
  email: z.string().email().optional(),
  phone: phoneSchema.nullable().optional(),
  role: z.enum(ROLES).optional(),
  is_active: z.number().int().min(0).max(1).optional(),
  password: z.string().min(6).optional(),
  pin: z.string().min(4).max(6).optional(),
  /** Admin unlock: clear the lock and its escalation tier without touching credentials. */
  unlock_lockout: z.boolean().optional(),
});

export const UpdateSettingSchema = z.object({
  value: z.string(),
});

export const ValidateSettingSchema = z.object({
  key: z.string().min(1),
  value: z.string(),
});

/** Check if role has at least the given privilege level */
export function hasRole(userRole: Role, requiredRole: Role): boolean {
  return ROLES.indexOf(userRole) <= ROLES.indexOf(requiredRole);
}

// Granular Permissions
export const PERMISSIONS = [
  'view:dashboard',
  'view:crm',
  'edit:crm',
  'view:financials',
  'edit:financials',
  'view:jobs',
  'edit:jobs',
  'view:schedule',
  'edit:schedule',
  'view:reports',
  'manage:users',
  'manage:settings',
  'view:compliance',
  'view:subcontractors',
  'manage:subcontractors',
  'manage:billing',
  'operate:fert',
  'manage:sequences',
  // Study material for applicator certification. Held by every human role — crew
  // train toward the $35/yr pesticide technician licence, which is what lets them
  // legally apply general-use product under a certified applicator.
  'view:training'
] as const;

export type Permission = typeof PERMISSIONS[number];

// Role to Permissions Mapping
export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  admin: [...PERMISSIONS], // Admin gets everything
  manager: [
    'view:dashboard', 'view:crm', 'edit:crm',
    'view:financials', 'edit:financials',
    'view:jobs', 'edit:jobs',
    'view:schedule', 'edit:schedule',
    'view:reports',
    'view:compliance', 'view:subcontractors', 'manage:subcontractors', 'manage:billing', 'manage:sequences', 'view:training', 'operate:fert'
  ],
  dispatcher: [
    'view:dashboard', 'view:crm', 'edit:crm',
    'view:jobs', 'edit:jobs',
    'view:schedule', 'edit:schedule', 'view:training', 'operate:fert'
  ],
  crew_lead: [
    'view:jobs', 'view:schedule', 'view:training'
  ],
  crew: [
    'view:schedule', 'view:training'
  ],
  fert_sub: [
    'view:schedule', 'view:training', 'operate:fert'
  ],
  viewer: [
    'view:dashboard', 'view:crm', 'view:jobs', 'view:schedule', 'view:reports',
    'view:compliance', 'view:training'
  ],
  service: [],
};

/** Check if a role has a specific permission */
export function hasPermission(role: Role, permission: Permission): boolean {
  if (role === 'admin') return true; // Safety net, admin always true
  return ROLE_PERMISSIONS[role]?.includes(permission) ?? false;
}
